import { Alert, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString } from "common";
import { Field, FieldProps } from "formik";
import React from "react";
import { Except } from "type-fest";
import { AuthorizeNetBillingDetails } from "types";
import { object } from "yup";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { CountrySelect } from "../Common/CountrySelect";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";

const validationSchema = object({
    firstName: requiredString(),
    lastName: requiredString(),
    address1: requiredString(),
    city: requiredString(),
    state: requiredString(),
    country: requiredString(),
});

interface AuthNetAdditionalBillingDetailsModalProps {
    isOpen: boolean;
    isSubmitting: boolean;
    onSubmit: (billingDetails: AuthorizeNetBillingDetails) => void;
    onClose: () => void;
    showError: boolean;
}

export const AuthNetAdditionalBillingDetailsModal = (props: AuthNetAdditionalBillingDetailsModalProps) => (
    <Modal isOpen={props.isOpen}>
        <ModalContent {...props} />
    </Modal>
);

const ModalContent = ({
    isSubmitting,
    onClose,
    showError,
    onSubmit,
}: Except<AuthNetAdditionalBillingDetailsModalProps, "isOpen">) => {
    const customerName = useCreatePurchaseStore((state) => state.purchase.customerName);

    const index = customerName.includes(" ") ? customerName.indexOf(" ") : customerName.length;

    const initialValues: AuthorizeNetBillingDetails = {
        firstName: customerName ? customerName.slice(0, Math.max(0, index)) : "",
        lastName: customerName ? customerName.slice(Math.max(0, index + 1)) : "",
        address1: "",
        city: "",
        state: "",
        country: "",
    };

    const handleSubmit = (values: AuthorizeNetBillingDetails) => {
        onSubmit(values);
    };

    return (
        <>
            <Modal.Header>Additional Billing Info Required</Modal.Header>

            <XolaFormik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit }) => (
                    <>
                        <Modal.Body>
                            {showError ? (
                                <Alert color="danger" className="mb-4 p-3 text-left">
                                    <p className="text-danger">
                                        Sorry, we were unable to process your payment. Please provide the billing
                                        details for your credit card and we will try again.
                                    </p>
                                </Alert>
                            ) : null}

                            <fieldset disabled={isSubmitting}>
                                <FormField name="firstName" label="First Name">
                                    <InputField isRequired placeholder="First Name" />
                                </FormField>

                                <FormField name="lastName" label="Last Name">
                                    <InputField isRequired placeholder="Last Name" />
                                </FormField>

                                <FormField name="address1" label="Address">
                                    <InputField isRequired placeholder="Address" />
                                </FormField>

                                <FormField name="city" label="City">
                                    <InputField isRequired placeholder="City" />
                                </FormField>

                                <FormField name="state" label="State">
                                    <InputField isRequired placeholder="State" />
                                </FormField>

                                <Field name="country">
                                    {({ form, meta }: FieldProps) => (
                                        <CountrySelect
                                            label="Country"
                                            error={meta.error}
                                            onChange={(option) => {
                                                form.setFieldValue("country", option.value);
                                            }}
                                        />
                                    )}
                                </Field>
                            </fieldset>
                        </Modal.Body>
                        <Modal.Footer>
                            <fieldset disabled={isSubmitting} className="flex justify-end space-x-2">
                                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                                <SubmitButton type="submit" isLoading={isSubmitting} onClick={handleSubmit}>
                                    Charge
                                </SubmitButton>
                            </fieldset>
                        </Modal.Footer>
                    </>
                )}
            </XolaFormik>
        </>
    );
};
